import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Divider, Icon } from 'semantic-ui-react'
import {
  ContactInfo,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  FieldWrapper,
  FormContainer,
  InfoText,
  SectionTitle,
  SubSectionTitle,
  TermsBox,
  TermsLink,
  TermsText,
  WelcomeHeader
} from './style'
import Dialog from '~/components/Dialog'
import { ThemeContext } from 'styled-components'
import { ClimaRegFarmerType } from '~/api/services/climaProgram'
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery'

interface Props {
  farmer: ClimaRegFarmerType
  handleClickTerms: (termsAccepted: boolean) => void
}

const WelcomePart: React.FC<Props> = ({ farmer, handleClickTerms }) => {
  const { t } = useTranslation()
  const { firstColor } = useContext(ThemeContext)

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [checkbox1, setCheckbox1] = useState(false)
  const [checkbox2, setCheckbox2] = useState(false)
  const [checkbox3, setCheckbox3] = useState(false)
  const [checkbox4, setCheckbox4] = useState(false)

  const {
    data: fileSource,
    isLoading: isLoadingFile
  } = ClimaProgramQuery.getAcceptTermsFile()

  const getAddress = () => {
    if (!farmer) return '-'
    const { streetName, streetNumber } = farmer
    if (streetName && streetNumber) return `${streetName} ${streetNumber}`
    if (streetName) return streetName
    return '-'
  }

  useEffect(() => {
    if (checkbox1 && checkbox2 && checkbox3 && checkbox4) {
      handleClickTerms(true)
    } else {
      handleClickTerms(false)
    }
  }, [checkbox1, checkbox2, checkbox3, checkbox4])

  function handleOpenPreview() {
    setIsPreviewOpen(true)
  }

  function handleClosePreview() {
    setIsPreviewOpen(false)
  }

  function renderAcceptTermsCheckboxes() {
    return <>
      <Checkbox
        checked={checkbox1}
        onChange={(e, data) => setCheckbox1(data.checked)}
        label={t('clima_checkbox_1')}
      />

      <div style={{ marginTop: '16px', marginBottom: '8px' }}>
        {t('clima_awareness_intro')}
      </div>
      <Divider />

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Checkbox
          checked={checkbox2}
          style={{ marginBottom: '1em' }}
          onChange={(e, data) => setCheckbox2(data.checked)}
          label={t('clima_checkbox_2')}
        />

        <Checkbox
          checked={checkbox3}
          style={{ marginBottom: '1em' }}
          onChange={(e, data) => setCheckbox3(data.checked)}
          label={t('clima_checkbox_3')}
        />

        <Checkbox
          checked={checkbox4}
          style={{ marginBottom: '1em' }}
          onChange={(e, data) => setCheckbox4(data.checked)}
          label={t('clima_checkbox_4')}
        />
      </div>
    </>
  }

  function renderAcceptTermsFile() {
    return <TermsBox $borderColor={firstColor}>
      <TermsText>
        {t('clima_terms_acceptance_prefix')}
        <TermsLink onClick={handleOpenPreview}>
          {t('Allgemeine Geschäftsbedingungen')}
        </TermsLink>
        {t('clima_terms_acceptance_suffix')}
      </TermsText>
    </TermsBox>
  }

  function renderPreviewContent() {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '500px', height: '93%' }}>
        <iframe
          src={fileSource ? `${fileSource}#toolbar=1&navpanes=0&view=FitH` : 'about:blank'}
          width='100%'
          height='100%'
          style={{ minHeight: 500 }}
        />
      </div>
    )
  }

  function renderPreviewTermsModal() {
    return <Dialog
      type='preview'
      textHeader={t('Allgemeine Geschäftsbedingungen')}
      heightContent='90%'
      heightDialog='80%'
      isDialogOpen={isPreviewOpen}
      noScrollingContent={false}
      style={{ width: '70%' }}
      isLoading={isLoadingFile}
      textClose={t('Close')}
      handleClose={handleClosePreview}
      textContent={renderPreviewContent()}
    />
  }

  function renderInfoSection() {
    return <FormContainer>
      <FieldsGrid>
        <FieldWrapper>
          <FieldLabel><Icon name='building outline' />{t('Company name')}:</FieldLabel>
          <FieldValue>{farmer?.companyName || '-'}</FieldValue>
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel><Icon name='id badge outline' />{t('EVS-Nr.')}:</FieldLabel>
          <FieldValue>{farmer?.agisId || '-'}</FieldValue>
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel><Icon name='id badge outline' />{t('AGIS Number')}:</FieldLabel>
          <FieldValue>{farmer?.agisId || '-'}</FieldValue>
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel><Icon name='map marker alternate' />{t('Address')}:</FieldLabel>
          <FieldValue>{getAddress()}</FieldValue>
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel><Icon name='mail outline' />{t('PLZ')}:</FieldLabel>
          <FieldValue>{farmer?.zip || '-'}</FieldValue>
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel><Icon name='point' />{t('City')}:</FieldLabel>
          <FieldValue>{farmer?.city || '-'}</FieldValue>
        </FieldWrapper>
      </FieldsGrid>
    </FormContainer>
  }

  function renderWelcomeHeader() {
    return <>
      <ContactInfo>
        {t('clima_contact_info')}
      </ContactInfo>

      <WelcomeHeader as='h1'>
        {t('clima_welcome_title')}
      </WelcomeHeader>

      <InfoText>{t('clima_intro_text')}</InfoText>
      <InfoText>{t('clima_part1_text')}</InfoText>
      <InfoText>{t('clima_part2_text')}</InfoText>
      <InfoText>{t('clima_part3_text')}</InfoText>
      <InfoText>{t('clima_after_registration_text')}</InfoText>
      <InfoText>{t('clima_before_start_text')}</InfoText>

      <SectionTitle as='h2'>
        {t('clima_registration_section_title')}
      </SectionTitle>

      <SubSectionTitle as='h3'>
        {t('Personal details')}
      </SubSectionTitle>
    </>
  }

  return <>

    {renderWelcomeHeader()}
    {renderInfoSection()}
    {renderAcceptTermsFile()}
    {renderAcceptTermsCheckboxes()}
    {renderPreviewTermsModal()}
  </>
}
export default WelcomePart
